import { CheckCircle2, FlaskConical, Plus, Trash2, XCircle } from "lucide-react";
import { useState } from "react";
import { Button, cx, Empty, ErrorNote, Modal, Spinner, toast } from "../components/ui";
import { api, useApi } from "../lib/api";

type Via = "actor" | "employeeKey" | "employee" | "workAssignment" | "workEmail";
interface Def {
  key: string;
  label: string;
  via: Via;
  field: string;
  custom?: boolean;
  verifiedBy: "waypoint" | "live";
  testedWith?: string;
}
interface Resolution {
  connId: string;
  actor: string;
  values: Record<string, string | null>;
  errors: Record<string, string>;
  at: string;
}

const VIA: Record<Via, string> = {
  actor: "Infor actor",
  employeeKey: "Employee link (Agent)",
  employee: "Employee",
  workAssignment: "Primary work assignment",
  workEmail: "Employee contact (work email)",
};

/** Tenant admin: identity attributes (design §6). */
export default function Attributes() {
  const { data, error, loading, reload } = useApi<{ catalog: Def[]; custom: Def[] }>("/admin/attributes");
  const conns = useApi<{ connections: { connId: string; name: string; credential?: unknown }[] }>("/admin/connections");
  const users = useApi<{ users: { email: string; name?: string; status: string }[] }>("/admin/users");
  const [adding, setAdding] = useState(false);
  const usable = (conns.data?.connections ?? []).filter((c) => c.credential);

  const remove = async (key: string) => {
    try {
      await api(`/admin/attributes/${key}`, { method: "DELETE" });
      toast(`${key} removed`);
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-ink">Identity attributes</h1>
        <p className="text-sm text-muted">Facts about each user read from their Infor records (through their mapped actor), which apps use to scope and fill in work.</p>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}

      <TryIt connections={usable} users={users.data?.users ?? []} defs={[...(data?.catalog ?? []), ...(data?.custom ?? [])]} />

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-ink">Catalog</h2>
          <span className="text-xs text-muted">Every field checked against Infor's delivered source in Waypoint</span>
        </div>
        {loading && !data ? <Spinner /> : <DefTable defs={data?.catalog ?? []} />}
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-ink">Custom attributes</h2>
          <Button variant="primary" disabled={!usable.length} onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden /> Add custom attribute
          </Button>
        </div>
        {data?.custom.length === 0 ? (
          <Empty icon={<FlaskConical className="size-8" />} title="No custom attributes">
            Add one for a field the catalog doesn't cover, such as a tenant extension field. It's tested live before it's saved.
          </Empty>
        ) : (
          <DefTable defs={data?.custom ?? []} onRemove={(k) => void remove(k)} />
        )}
      </section>

      {adding && <CustomForm connections={usable} users={users.data?.users ?? []} onClose={() => setAdding(false)} onSaved={() => (setAdding(false), void reload())} />}
    </div>
  );
}

function DefTable({ defs, onRemove }: { defs: Def[]; onRemove?: (key: string) => void }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-surface">
      <table className="w-full text-sm">
        <thead className="text-left text-muted border-b border-line">
          <tr>
            <th className="px-4 py-2 font-medium">Attribute</th>
            <th className="px-4 py-2 font-medium">Read from</th>
            <th className="px-4 py-2 font-medium">Field</th>
            {onRemove && <th className="px-4 py-2" />}
          </tr>
        </thead>
        <tbody>
          {defs.map((d) => (
            <tr key={d.key} className="border-b border-line last:border-0">
              <td className="px-4 py-2.5">
                <div className="text-ink">{d.label}</div>
                <code className="text-xs text-muted">{d.key}</code>
              </td>
              <td className="px-4 py-2.5 text-ink-2">{VIA[d.via]}</td>
              <td className="px-4 py-2.5">
                <code className="text-xs">{d.field === "PersonName" ? "PersonName.GivenName + FamilyName" : d.field}</code>
                {d.testedWith && <div className="text-xs text-muted">Tested with {d.testedWith}</div>}
              </td>
              {onRemove && (
                <td className="px-4 py-2.5 text-right">
                  <Button variant="ghost" aria-label={`Remove ${d.key}`} onClick={() => onRemove(d.key)}>
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pickers({ connections, users, connId, email, setConnId, setEmail }: { connections: { connId: string; name: string }[]; users: { email: string; name?: string }[]; connId: string; email: string; setConnId: (v: string) => void; setEmail: (v: string) => void }) {
  const sel = "h-9 rounded-lg border border-line bg-surface px-2 text-sm";
  return (
    <>
      <label className="text-sm">
        <span className="block text-ink-2 font-medium mb-1">Connection</span>
        <select className={sel} value={connId} onChange={(e) => setConnId(e.target.value)}>
          <option value="">Choose…</option>
          {connections.map((c) => (
            <option key={c.connId} value={c.connId}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        <span className="block text-ink-2 font-medium mb-1">User</span>
        <select className={sel} value={email} onChange={(e) => setEmail(e.target.value)}>
          <option value="">Choose…</option>
          {users.map((u) => (
            <option key={u.email} value={u.email}>
              {u.name ? `${u.name} (${u.email})` : u.email}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}

function TryIt({ connections, users, defs }: { connections: { connId: string; name: string }[]; users: { email: string; name?: string }[]; defs: Def[] }) {
  const [connId, setConnId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Resolution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await api<Resolution>("/admin/attributes/test", { body: { connId, email } }));
    } catch (e) {
      setError((e as Error).message);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-xl border border-line bg-surface p-5">
      <h2 className="font-display font-semibold text-ink mb-3">Try it for a user</h2>
      <div className="flex flex-wrap items-end gap-3">
        <Pickers connections={connections} users={users} connId={connId} email={email} setConnId={setConnId} setEmail={setEmail} />
        <Button variant="primary" busy={busy} disabled={!connId || !email} onClick={() => void run()}>
          Read from Infor
        </Button>
      </div>
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      {result && (
        <div className="mt-4">
          <div className="text-xs text-muted mb-2">
            Actor {result.actor} on {result.connId} · {new Date(result.at).toLocaleTimeString()}
          </div>
          <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2 text-sm">
            {defs.map((d) => {
              const err = result.errors[d.key];
              const v = result.values[d.key];
              return (
                <li key={d.key} className="flex items-start gap-2">
                  {err ? <XCircle className="size-4 mt-0.5 text-bad shrink-0" aria-hidden /> : <CheckCircle2 className={cx("size-4 mt-0.5 shrink-0", v ? "text-good" : "text-muted")} aria-hidden />}
                  <span className="text-ink-2 w-44 shrink-0">{d.label}</span>
                  <span className={cx("min-w-0 break-words", err ? "text-bad" : v ? "text-ink" : "text-muted")}>{err ?? v ?? "blank"}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function CustomForm({ connections, users, onClose, onSaved }: { connections: { connId: string; name: string }[]; users: { email: string; name?: string }[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ key: "", label: "", via: "employee" as Via, field: "" });
  const [connId, setConnId] = useState(connections[0]?.connId ?? "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ sample: string | null }>("/admin/attributes", { body: { ...f, test: { connId, email } } });
      toast(`Saved. ${email} has ${f.key} = ${r.sample ?? "blank"}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  return (
    <Modal
      title="Add custom attribute"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!connId || !email} onClick={() => void save()}>
            Test and save
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="text-ink-2 font-medium">Key</span>
            <input className={input} value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="unionCode" />
          </label>
          <label>
            <span className="text-ink-2 font-medium">Label</span>
            <input className={input} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Union" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="text-ink-2 font-medium">Read from</span>
            <select className={input} value={f.via} onChange={(e) => setF({ ...f, via: e.target.value as Via })}>
              {(["actor", "employee", "workAssignment"] as Via[]).map((v) => (
                <option key={v} value={v}>
                  {VIA[v]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-ink-2 font-medium">Infor field</span>
            <input className={input} value={f.field} onChange={(e) => setF({ ...f, field: e.target.value })} placeholder="Union" />
          </label>
        </div>
        <p className="text-xs text-muted">Use the field's Landmark name; dot group members (ContactInfo.EmailAddress). Tenant extension fields aren't in Waypoint, so the live test below is what proves them.</p>
        <fieldset className="rounded-lg bg-surface-2 p-3 flex flex-wrap gap-3">
          <legend className="sr-only">Live test</legend>
          <Pickers connections={connections} users={users} connId={connId} email={email} setConnId={setConnId} setEmail={setEmail} />
        </fieldset>
      </div>
    </Modal>
  );
}
